import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import {
  ToolbarButton,
  type GridColDef,
  type GridRowId,
  type GridRowSelectionModel,
} from '@mui/x-data-grid';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  CreateProvisionPayoutDocument,
  GetAccountingDocumentsDocument,
  GetTenantsDocument,
  type AccountingDocumentFilterInput,
  type GetAccountingDocumentsQuery,
  type GetTenantsQuery,
  type TenantType,
} from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { useNotification } from '@/app/contexts/NotificationContext';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';

interface ResellerRow {
  id: string;
  tenantNumber: string | number;
  name: string;
  status: string;
  resellerId: string | number;
  parentId: string;
  discountPercent: number | null;
  createdAt: string;
  [key: string]: unknown;
}

function createEmptySelection(): GridRowSelectionModel {
  return { type: 'include', ids: new Set<GridRowId>() };
}

export default function Resellers() {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [page, setPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>(() =>
    createEmptySelection(),
  );
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('rowsPerPage');
    return saved ? parseInt(saved, 10) : 25;
  });

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatCurrency = (amount?: number | null, currency?: string | null) => {
    if (amount == null) return '–';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'EUR' }).format(
      amount,
    );
  };
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';

  const { data, loading } = useQuery<GetTenantsQuery>(GetTenantsDocument, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE, where: { type: { eq: 'RESELLER' as TenantType } } },
  });
  const [createPayout, { loading: payoutLoading }] = useMutation(CreateProvisionPayoutDocument);

  const tenants = data?.tenants?.nodes ?? [];
  const totalCount = data?.tenants?.totalCount ?? 0;

  const rows: ResellerRow[] = tenants.map((tenant) => ({
    id: tenant.id,
    tenantNumber: tenant.tenantNumber,
    name: tenant.name,
    status: tenant.status,
    resellerId: tenant.resellerId ?? '–',
    parentId: tenant.parentId ?? '–',
    discountPercent: tenant.discountPercent,
    createdAt: formatDate(tenant.createdAt),
  }));

  const columns: GridColDef[] = [
    { field: 'tenantNumber', headerName: t('resellers.tenantNumber'), width: 130 },
    { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 220 },
    { field: 'status', headerName: t('common.status'), width: 140 },
    { field: 'resellerId', headerName: t('common.resellerId'), width: 130 },
    { field: 'parentId', headerName: t('resellers.parentId'), minWidth: 260 },
    { field: 'createdAt', headerName: t('common.created'), width: 140 },
  ];

  const selectedResellerId =
    selectedRows.type === 'include' ? Array.from(selectedRows.ids)[0]?.toString() : undefined;
  const selectedReseller = rows.find((row) => row.id === selectedResellerId);

  const payoutPreviewFilter = useMemo<AccountingDocumentFilterInput | undefined>(
    () =>
      selectedResellerId
        ? {
            provisionActive: { eq: true },
            provisionCreditNoteId: { eq: null },
            state: { eq: 'PAID' },
          }
        : undefined,
    [selectedResellerId],
  );

  const [loadPayoutDocuments, { data: payoutDocsData, loading: payoutDocsLoading }] =
    useLazyQuery<GetAccountingDocumentsQuery>(GetAccountingDocumentsDocument, {
      fetchPolicy: 'network-only',
    });

  const payoutDocuments = payoutDocsData?.accountingDocuments?.nodes ?? [];
  const discountFraction = (selectedReseller?.discountPercent ?? 0) / 100;
  const payoutTotal = payoutDocuments.reduce((sum, doc) => {
    const sign = doc.type === 'CREDIT_NOTE' ? -1 : 1;
    const amount = Math.round(doc.amountNet * discountFraction * sign * 100) / 100;
    return sum + amount;
  }, 0);
  const payoutCurrency = payoutDocuments[0]?.currencyCode ?? 'EUR';

  const handleOpenPayoutDialog = () => {
    if (!payoutPreviewFilter) return;
    setPayoutDialogOpen(true);
    void loadPayoutDocuments({
      variables: {
        first: GRAPHQL_MAX_PAGE_SIZE,
        resellerTenantId: selectedResellerId,
        where: payoutPreviewFilter,
      },
    });
  };

  const handlePayout = async () => {
    if (!selectedResellerId) return;
    try {
      const { data } = await createPayout({
        variables: { input: { resellerTenantId: selectedResellerId } },
      });
      const cn = data?.createProvisionPayout?.accountingDocument;
      showSuccess(
        cn?.number
          ? t('tenantDetail.payoutCreated', { number: cn.number })
          : t('tenantDetail.payoutCreatedGeneric'),
      );
      setPayoutDialogOpen(false);
      setSelectedRows(createEmptySelection());
    } catch (err) {
      showError(err instanceof Error ? err.message : t('errors.generic'));
    }
  };

  const toolbarActions = selectedResellerId ? (
    <ToolbarButton
      onClick={handleOpenPayoutDialog}
      disabled={payoutLoading}
      aria-label={t('tenantDetail.payoutConfirm')}
    >
      <RequestQuoteIcon fontSize="small" color="primary" />
      <Typography
        variant="body2"
        color="primary"
        sx={{ fontWeight: 600, ml: 0.5, display: { xs: 'none', sm: 'block' } }}
      >
        {t('tenantDetail.payoutConfirm')}
      </Typography>
    </ToolbarButton>
  ) : null;

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{t('resellers.title')}</Typography>
      </Box>
      <DataTable<ResellerRow>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
        selectable
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        selectionMode="single"
        toolbarActions={toolbarActions}
      />
      <Dialog
        open={payoutDialogOpen}
        onClose={() => setPayoutDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t('resellers.payoutDialogTitle', { name: selectedReseller?.name ?? '–' })}
        </DialogTitle>
        <DialogContent dividers>
          {payoutDocsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : payoutDocuments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('resellers.payoutNoDocuments')}
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('resellers.payoutDocumentCount', { count: payoutDocuments.length })}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.number')}</TableCell>
                    <TableCell>{t('common.tenant')}</TableCell>
                    <TableCell>{t('common.created')}</TableCell>
                    <TableCell align="right">{t('common.net')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payoutDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>{doc.number}</TableCell>
                      <TableCell>{doc.tenant.name}</TableCell>
                      <TableCell>{formatDate(doc.createdAt)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(doc.amountNet, doc.currencyCode)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography fontWeight={700}>{t('resellers.payoutTotal')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700}>
                        {formatCurrency(payoutTotal, payoutCurrency)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayoutDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handlePayout}
            disabled={payoutLoading || payoutDocsLoading || payoutDocuments.length === 0}
          >
            {t('tenantDetail.payoutConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
